import clsx from 'clsx';
import type { ReactNode, Ref } from 'react';
import { forwardRef, Fragment, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Observable, Subject, takeUntil } from 'rxjs';
import { OdinDataSender } from '../../common/data-retrieval/odin-data-sender';
import useTestTag from '../../common/hooks/use-test-tag';
import Button from '../button/button';
import { getAllElementStyleValues, mergeStyling } from '../common-functions';
import { useIsMountedRef } from '../common-hooks';
import type { ModalDialogButtonSetting, ModalDialogResult } from '../common-interfaces';
import OdinTransition from '../common/odin-transition/odin-transition.component';
import { DomHandler } from '../dom-handler';
import { ScreenPosition } from '../enums';
import { bindDocumentKeyListener, unbindDocumentClickListener, unbindDocumentKeyListener } from '../event-handlers';
import OdinIcon from '../odin-icon/odin-icon.component';
import type { ModalDialogContextProps } from './modal-dialog.context';
import { ModalDialogContext } from './modal-dialog.context';

export interface ModalDialogProps {
  visible?: boolean;
  displayPosition?: ScreenPosition;
  transitionDuration?: number;
  showTransitionOptions?: string;
  hideTransitionOptions?: string;
  minDialogWidth?: number | string;
  maxDialogWidth?: number | string;
  minDialogHeight?: number | string;
  maxDialogHeight?: number | string;
  fullScreen?: boolean;
  showCloseIcon?: boolean;
  closeOnEscape?: boolean;
  mountOnEnter?: boolean;
  unmountOnExit?: boolean;
  header: string | JSX.Element;
  footer?: string | JSX.Element;
  shown?: () => void;
  hidden?: () => void;
  buttons?: Array<ModalDialogButtonSetting>;
  buttonsCloseModal?: boolean;
  buttonType?: 'button' | 'submit';
  children: ReactNode | Array<ReactNode>;
  shouldCheckZIndex?: boolean;
  renderChildrenLazily?: boolean;
  contentClassName?: string;
}

export interface ModalDialogRef {
  show: () => undefined | Observable<ModalDialogResult>;
  close: () => void;
  subscribeToDialogResult: () => Observable<ModalDialogResult>;
  dataSender?: OdinDataSender<ModalDialogContextProps>;
}

export interface ModalDialogListeners {
  clickListener?: (event: MouseEvent) => void;
  keyListener?: (event: KeyboardEvent) => void;
}

function ModalDialog(props: ModalDialogProps, ref: Ref<ModalDialogRef>) {
  const {
    visible = false,
    displayPosition = ScreenPosition.Center,
    transitionDuration = 400,
    showTransitionOptions = 'linear',
    hideTransitionOptions = 'linear',
    minDialogWidth = 300,
    maxDialogWidth = 700,
    minDialogHeight,
    maxDialogHeight,
    fullScreen = false,
    showCloseIcon = true,
    closeOnEscape = true,
    mountOnEnter = true,
    unmountOnExit = true,
    header,
    footer,
    shown,
    hidden,
    buttons,
    buttonsCloseModal = true,
    buttonType = 'button',
    children,
    shouldCheckZIndex = false,
    renderChildrenLazily = false,
    contentClassName,
  } = props;

  const [top, setTop] = useState(0);
  const [left, setLeft] = useState(0);
  const [underlayZIndex, setUnderlayZIndex] = useState(-1);
  const [dialogZIndex, setDialogZIndex] = useState(-1);
  const [visibility, setVisibility] = useState(visible || false);
  const [modalData, setModalData] = useState<ModalDialogContextProps>({ data: {} });

  const destroySubject = useRef(new Subject<void>());
  const dataSender = useRef<OdinDataSender<ModalDialogContextProps>>();
  const resizeObserver = useRef<ResizeObserver>();
  const dialogRef = useRef<HTMLDivElement>(null);
  const listenerRef = useRef<ModalDialogListeners>({});
  const dialogResultRef = useRef<Subject<ModalDialogResult>>(new Subject<ModalDialogResult>());
  const isMounted = useIsMountedRef();

  const defaultUnderlayModalDialogZIndex = useRef(101);
  const defaultModalDialogZIndex = useRef(102);

  useEffect(() => {
    new Observable<ModalDialogContextProps>((subscriber) => {
      dataSender.current = new OdinDataSender<ModalDialogContextProps>(subscriber);
    })
      .pipe(takeUntil(destroySubject.current))
      .subscribe((data) => setModalData(data));

    resizeObserver.current = new ResizeObserver(() => {
      if (!fullScreen && dialogRef.current && isMounted.current) {
        const { top, left } = DomHandler.positionToScreen(dialogRef.current, displayPosition);
        setTop(top);
        setLeft(left);
      }
    });

    // Capture ref values for cleanup
    const destroySubjectCurrent = destroySubject.current;
    const resizeObserverCurrent = resizeObserver.current;

    return () => {
      destroySubjectCurrent.next();
      destroySubjectCurrent.complete();

      resizeObserverCurrent?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Mount-only effect for resize observer setup
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      setVisibility(visible);
      if (visible) {
        setZIndexValues();
      } else {
        setUnderlayZIndex(-1);
        setDialogZIndex(-1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setZIndexValues is stable, isMounted is a ref
  }, [visible]);

  const tagCreator = useTestTag('modal');

  const setZIndexValues = useCallback(() => {
    let allZIndexes: Array<number> = [];

    if (shouldCheckZIndex) {
      allZIndexes = getAllElementStyleValues('zIndex', (styleValue) => {
        const childZIndex = parseInt(styleValue);
        return childZIndex >= 100;
      }).map((item) => parseInt(item));
    }

    if (allZIndexes.length > 0) {
      setUnderlayZIndex(Math.max(...allZIndexes) + 1);
      setDialogZIndex(Math.max(...allZIndexes) + 2);
    } else {
      setUnderlayZIndex(defaultUnderlayModalDialogZIndex.current);
      setDialogZIndex(defaultModalDialogZIndex.current);
    }
  }, [shouldCheckZIndex]);

  const onEntering = useCallback(() => {
    if (!dialogRef.current || !isMounted.current) return;

    if (!fullScreen) {
      const { top, left } = DomHandler.positionToScreen(dialogRef.current, displayPosition);
      setTop(top);
      setLeft(left);
    } else {
      setTop(0);
      setLeft(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isMounted is a ref, not a dependency
  }, [fullScreen, displayPosition]);

  const onEntered = () => {
    if (!dialogRef.current || !isMounted.current) return;

    resizeObserver.current?.observe(dialogRef.current);
    shown?.();

    if (underlayZIndex === -1 || dialogZIndex === -1) {
      setUnderlayZIndex(defaultUnderlayModalDialogZIndex.current);
      setDialogZIndex(defaultModalDialogZIndex.current);
    }

    if (closeOnEscape) {
      listenerRef.current.keyListener = bindDocumentKeyListener(['Escape'], (isKeyNamePressed) => {
        if (isKeyNamePressed) {
          setVisibility(false);
        }
      });
    }
  };

  const onExit = useCallback(() => {
    if (listenerRef.current.clickListener) {
      unbindDocumentClickListener(listenerRef.current.clickListener);
    }
    if (listenerRef.current.keyListener) {
      unbindDocumentKeyListener(listenerRef.current.keyListener);
    }

    if (dialogRef.current) {
      resizeObserver.current?.unobserve(dialogRef.current);
    }
  }, []);

  const onExited = () => {
    if (!isMounted.current) return;

    setDialogZIndex(-1);
    setUnderlayZIndex(-1);

    hidden?.();
  };

  const onCloseClick = useCallback(() => {
    close();
    dialogResultRef.current.next({
      buttonName: 'close',
    });
  }, []);

  const onResultButtonClick = useCallback(
    (buttonName: string, closeModalOnClick = true) => {
      if (!isMounted.current) return;

      if (buttonsCloseModal && closeModalOnClick) {
        setVisibility(false);
      }

      dialogResultRef.current.next({
        buttonName,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isMounted is a ref that doesn't change
    [buttonsCloseModal]
  );

  const show = useCallback(() => {
    if (!isMounted.current) return;

    setVisibility(true);
    setZIndexValues();
    return dialogResultRef.current.asObservable();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isMounted is a ref, setZIndexValues is stable
  }, [setZIndexValues]);

  const close = () => setVisibility(false);

  const subscribeToDialogResult = () => dialogResultRef.current.asObservable();

  useImperativeHandle(ref, () => ({
    show,
    close,
    subscribeToDialogResult,
    dataSender: dataSender.current,
  }));

  const renderHeader = useCallback(() => {
    return (
      <div className="odin-items-top odin-flex odin-shrink odin-px-4 odin-pb-2 odin-pt-3">
        <div className="odin-grow odin-truncate odin-text-base odin-font-medium odin-text-mono-0">{header}</div>
        {showCloseIcon && (
          <div
            className="-odin-mr-2 -odin-mt-1 odin-flex odin-shrink odin-cursor-pointer odin-items-start odin-pl-2 odin-text-primary-2"
            onClick={onCloseClick}
            ref={tagCreator('close-icon')}
            data-testid="modal-close-icon"
          >
            <OdinIcon icon="X" />
          </div>
        )}
      </div>
    );
  }, [header, showCloseIcon, onCloseClick, tagCreator]);

  const renderFooter = useCallback(() => {
    if (buttons || footer) {
      let renderedButtons: JSX.Element = <></>;
      let leftAlignedButtons: ModalDialogButtonSetting[] = [];

      if (buttons) {
        leftAlignedButtons = buttons.filter((button) => button.alignment === 'left');
        const rightAlignedButtons = buttons.filter((button) => button.alignment !== 'left');

        const buttonsWrapperClasses = clsx('odin-flex odin-items-center odin-justify-between', {
          'odin-w-full': leftAlignedButtons.length > 0 || !footer,
        });

        renderedButtons = (
          <div className={buttonsWrapperClasses}>
            <div className="odin-flex odin-items-center odin-justify-center odin-gap-x-2 odin-self-start">
              {leftAlignedButtons.map((button) => (
                <Fragment key={button.name}>{renderFooterButton(button)}</Fragment>
              ))}
            </div>
            <div className="odin-flex odin-items-center odin-justify-center odin-gap-x-2 odin-self-end">
              {rightAlignedButtons.map((button) => (
                <Fragment key={button.name}>{renderFooterButton(button)}</Fragment>
              ))}
            </div>
          </div>
        );
      }

      const footerClasses = clsx(
        'odin-truncate odin-p-1',
        { 'odin-grow': leftAlignedButtons.length > 0 },
        { 'odin-flex-1': leftAlignedButtons.length === 0 }
      );

      return (
        <div className="odin-flex odin-border-t odin-border-solid odin-border-primary-5 odin-p-4">
          {footer && <div className={footerClasses}>{footer}</div>}
          {renderedButtons}
        </div>
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- renderFooterButton is defined after this callback
  }, [buttons, footer]);

  const renderFooterButton = useCallback(
    (button: ModalDialogButtonSetting) => (
      <div className="odin-flex-auto" ref={tagCreator(`${button.text?.replace(/\s+/g, '-').toLowerCase()}-button`)}>
        <Button
          htmlType={buttonType}
          classNames={button.classNames}
          type={button.type}
          variant={button.variant}
          form={button.form}
          disabled={button.disabled}
          onClick={() => {
            if (isMounted.current) {
              button.onClick?.();
              onResultButtonClick(button.name, button.closeModalOnClick);
            }
          }}
        >
          {button.text}
        </Button>
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isMounted is a ref that doesn't change
    [buttonType, onResultButtonClick, tagCreator]
  );

  const createDialog = () => {
    const marginOffset = fullScreen ? 0 : 32;
    const baseStyles = {
      maxWidth: maxDialogWidth,
      maxHeight: maxDialogHeight || `calc(100vh - ${marginOffset * 2}px)`,
      minWidth: minDialogWidth,
      minHeight: minDialogHeight,
    };

    return (
      <OdinTransition
        start={visibility}
        timeout={transitionDuration}
        showTransitionOptions={showTransitionOptions}
        hideTransitionOptions={hideTransitionOptions}
        mountOnEnter={mountOnEnter}
        unmountOnExit={unmountOnExit}
        onEntering={onEntering}
        onEntered={onEntered}
        onExit={onExit}
        onExited={onExited}
      >
        <>
          <div>
            <div
              className="odin-fixed odin-left-0 odin-top-0 odin-h-full odin-w-full odin-bg-mono-0"
              onClick={() => {
                setVisibility(false);
              }}
              style={{
                zIndex: underlayZIndex,
                opacity: 0.5,
              }}
              data-testid="modal-backdrop"
            />
            {(renderChildrenLazily ? visibility : true) && (
              <div
                className={clsx('odin-fixed odin-max-w-full', {
                  'odin-h-full odin-w-full': fullScreen,
                })}
                style={{
                  padding: marginOffset,
                  zIndex: dialogZIndex,
                  top,
                  left,
                }}
                ref={dialogRef}
              >
                <div
                  className={clsx('odin-flex odin-flex-col odin-bg-mono-1 odin-shadow-lg odin-rounded-md', {
                    'odin-relative odin-left-[2.5%] odin-top-[2.5%] odin-h-[90vh] odin-w-[95vw] sm:odin-h-[95vh]':
                      fullScreen,
                  })}
                  style={fullScreen ? {} : { ...baseStyles }}
                  ref={tagCreator('container')}
                >
                  {renderHeader()}
                  <ModalDialogContext.Provider value={modalData}>
                    <div
                      className={mergeStyling(
                        'custom-scroll odin-grow odin-overflow-auto odin-px-4 odin-pb-4',
                        contentClassName
                      )}
                      ref={tagCreator('content')}
                    >
                      {children}
                    </div>
                  </ModalDialogContext.Provider>
                  {renderFooter()}
                </div>
              </div>
            )}
          </div>
        </>
      </OdinTransition>
    );
  };

  return ReactDOM.createPortal(createDialog(), document.body);
}

export default forwardRef(ModalDialog);
